import {
  EnvironmentProviders,
  importProvidersFrom,
  inject,
  StaticProvider,
} from '@angular/core';
import {
  getModuleGroupById,
  registerApplication,
  SOFTLINE_CONFIG_DASHBOARD_COMPONENT,
} from '@softline/application';
import { SHELL_SEARCH_ACTION, ShellSearchAction } from '@softapps/core';
import { StempelerfassungQuickCreateService } from './services/stempelerfassung-quick-create.service';
import {
  ConnectionHttpService,
  SOFTLINE_SERVICE_HTTP,
  SOFTLINE_STORE_ENTITY_SERVICE,
  SOFTLINE_STORE_TEMPLATE_SERVICE,
  SOFTLINE_STORE_VALIDATION_SERVICE,
  SoftlineEntityService,
  SoftlineTemplateService,
  SoftlineValidationService,
  StoreModule,
} from '@softline/core';
import { default as de } from '../i18n/de.json';
import { default as en } from '../i18n/en.json';
import {
  SOFTLINE_API_EMPLOYEES,
  SOFTLINE_API_TIMESTAMP,
  SOFTLINE_API_TIMESTAMP_CODE,
  SOFTLINE_API_TIMESTAMP_TEMPLATE,
  SOFTLINE_API_TIMESTAMP_VALIDATION,
} from './stempelerfassung.api';
import { SOFTLINE_PERMISSION_TIMESTAMP } from './stempelerfassung.permissions';
import {
  SOFTLINE_FEATURE_TIMESTAMP,
  SOFTLINE_FEATURE_TIMESTAMP_CODE,
  SOFTLINE_FEATURE_TIMESTAMP_CURRENT_EMPLOYEE,
  SOFTLINE_FEATURE_TIMESTAMP_EMPLOYEE,
  TimestampCodeStore,
  TimestampEmployeeStore,
  TimestampStore,
} from './stempelerfassung.shared';
import { TimestampCurrentEmployeeStore } from './store/current-employee.store';
import { StempelerfassungWidget } from './widgets/stempelerfassung/stempelerfassung.widget';
import { WorkHoursWidget } from './widgets/work-hours/work-hours.widget';

export function provideStempelerfassungSearchAction() {
  return {
    provide: SHELL_SEARCH_ACTION,
    multi: true,
    useFactory: (): ShellSearchAction => {
      const quickCreate = inject(StempelerfassungQuickCreateService);
      return {
        title: 'Stempel erfassen',
        category: 'Stempelerfassung',
        icon: 'fa-regular fa-stopwatch',
        permission: SOFTLINE_PERMISSION_TIMESTAMP,
        action: () => quickCreate.recordTimestamp(),
        url: '/stempelerfassung',
      };
    },
  };
}

export const stempelerfassungWidgetProviders = [
  {
    provide: SOFTLINE_CONFIG_DASHBOARD_COMPONENT,
    multi: true,
    useValue: {
      component: WorkHoursWidget,
      permission: SOFTLINE_PERMISSION_TIMESTAMP,
    },
  },
  {
    provide: SOFTLINE_CONFIG_DASHBOARD_COMPONENT,
    multi: true,
    useValue: {
      component: StempelerfassungWidget,
      permission: SOFTLINE_PERMISSION_TIMESTAMP,
    },
  },
];

export const stempelerfassungProviders: (StaticProvider | EnvironmentProviders)[] = [
  registerApplication({
    name: 'stempelerfassung',
    title: '#TIMESTAMP.APP',
    icon: 'fa-regular fa-stopwatch',
    color: 'bg-[#0177BF] text-white',
    route: '/stempelerfassung',
    permission: SOFTLINE_PERMISSION_TIMESTAMP,
    translations: { de, en },
    group: getModuleGroupById(8), // Personalmanagement
  }),
  importProvidersFrom(
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_TIMESTAMP_EMPLOYEE,
      feature: TimestampEmployeeStore.feature,
      providers: [
        {
          provide: SOFTLINE_STORE_ENTITY_SERVICE,
          useFactory: (service: ConnectionHttpService) =>
            new SoftlineEntityService(service, SOFTLINE_API_EMPLOYEES),
          deps: [SOFTLINE_SERVICE_HTTP],
        },
      ],
    }),
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_TIMESTAMP,
      feature: TimestampStore.feature,
      providers: [
        {
          provide: SOFTLINE_STORE_ENTITY_SERVICE,
          useFactory: (service: ConnectionHttpService) =>
            new SoftlineEntityService(service, SOFTLINE_API_TIMESTAMP),
          deps: [SOFTLINE_SERVICE_HTTP],
        },
        {
          provide: SOFTLINE_STORE_VALIDATION_SERVICE,
          useFactory: (service: ConnectionHttpService) =>
            new SoftlineValidationService(service, SOFTLINE_API_TIMESTAMP_VALIDATION),
          deps: [SOFTLINE_SERVICE_HTTP],
        },
        {
          provide: SOFTLINE_STORE_TEMPLATE_SERVICE,
          useFactory: (service: ConnectionHttpService) =>
            new SoftlineTemplateService(service, SOFTLINE_API_TIMESTAMP_TEMPLATE),
          deps: [SOFTLINE_SERVICE_HTTP],
        },
      ],
    }),
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_TIMESTAMP_CODE,
      feature: TimestampCodeStore.feature,
      providers: [
        {
          provide: SOFTLINE_STORE_ENTITY_SERVICE,
          useFactory: (service: ConnectionHttpService) =>
            new SoftlineEntityService(service, SOFTLINE_API_TIMESTAMP_CODE),
          deps: [SOFTLINE_SERVICE_HTTP],
        },
      ],
    }),
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_TIMESTAMP_CURRENT_EMPLOYEE,
      feature: TimestampCurrentEmployeeStore.feature,
      providers: [
        {
          provide: SOFTLINE_STORE_ENTITY_SERVICE,
          useFactory: (service: ConnectionHttpService) =>
            new SoftlineEntityService(service, SOFTLINE_API_EMPLOYEES),
          deps: [SOFTLINE_SERVICE_HTTP],
        },
      ],
    })
  ),
];
